/*
 * Purpose: Mission workspace — runtime + map tool registration for unified sub-nav
 * Last modified: 2026-07-01 | Agent
 * Completeness: 100
 */
import * as React from 'react';
import type { BlockTemplate } from '@/lib/blockPalette';
import type { MapFlowOrientation } from '@/stores/workflowStore';
import type { ResolvedMapFlowOrientation } from '@/lib/mapFlowOrientation';

export type MissionRuntimeRegistration = {
  isExecuting: boolean;
  onExecute: () => void | Promise<void>;
  onStop: () => void;
  onOpenAssistant?: () => void;
};

export type MissionMapToolsRegistration = {
  zoom: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onAddBlock: (template: BlockTemplate) => void;
  flowOrientation: MapFlowOrientation;
  effectiveFlowOrientation: ResolvedMapFlowOrientation;
  onFlowOrientationChange?: (orientation: MapFlowOrientation) => void;
};

type MissionWorkspaceValue = {
  runtime: MissionRuntimeRegistration | null;
  setRuntime: (value: MissionRuntimeRegistration | null) => void;
  mapTools: MissionMapToolsRegistration | null;
  setMapTools: (value: MissionMapToolsRegistration | null) => void;
  onNewFlow?: () => void;
  setOnNewFlow: (handler: (() => void) | undefined) => void;
};

const MissionWorkspaceContext = React.createContext<MissionWorkspaceValue | null>(null);

export function MissionWorkspaceProvider({ children }: { children: React.ReactNode }) {
  const [runtime, setRuntime] = React.useState<MissionRuntimeRegistration | null>(null);
  const [mapTools, setMapTools] = React.useState<MissionMapToolsRegistration | null>(null);
  const [onNewFlow, setOnNewFlowState] = React.useState<(() => void) | undefined>();

  const setOnNewFlow = React.useCallback((handler: (() => void) | undefined) => {
    setOnNewFlowState(() => handler);
  }, []);

  const value = React.useMemo(
    () => ({
      runtime,
      setRuntime,
      mapTools,
      setMapTools,
      onNewFlow,
      setOnNewFlow,
    }),
    [runtime, mapTools, onNewFlow, setOnNewFlow]
  );

  return (
    <MissionWorkspaceContext.Provider value={value}>{children}</MissionWorkspaceContext.Provider>
  );
}

export function useMissionWorkspace() {
  const ctx = React.useContext(MissionWorkspaceContext);
  if (!ctx) {
    throw new Error('useMissionWorkspace must be used within MissionWorkspaceProvider');
  }
  return ctx;
}

export function useMissionWorkspaceOptional() {
  return React.useContext(MissionWorkspaceContext);
}
